import React, { useState, useEffect } from 'react'
import {
  Table,
  Button,
  Space,
  Input,
  Select,
  Row,
  Col,
  Card,
  Tag,
  Popconfirm,
  message,
  Badge,
  DatePicker,
} from 'antd'
import {
  PlusOutlined,
  DeleteOutlined,
  CheckOutlined,
  BellOutlined,
  MailOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SendOutlined,
  CalendarOutlined,
} from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { NotificationItem, notificationStore, EventTrigger, DeliveryChannel, isUnread, isForUser } from './notificationStore'
import { authStore } from '@/context/auth'
import { NotificationEngine } from '@/utils/notificationEngine'
import TableSkeleton from '@/components/TableSkeleton'
import KpiCardRow from '@/components/KpiCardRow'

const TRIGGER_COLORS: Record<string, string> = {
  INVOICE_DUE: 'orange',
  INVOICE_OVERDUE: 'red',
  LEAD_ASSIGNED: 'blue',
  APPROVAL_REJECTED: 'red',
  PAYMENT_RECEIVED: 'green',
  AGREEMENT_EXPIRY: 'gold',
  PROPOSAL_EXPIRY: 'gold',
  SYSTEM_ALERT: 'purple',
  ALLOCATION_PENDING: 'volcano',
  INVOICE_GENERATED: 'cyan',
}

const CHANNEL_COLORS: Record<string, string> = {
  'In-App': 'geekblue',
  'Email': 'cyan',
  'WhatsApp': 'green',
}

const NotificationList: React.FC = () => {
  const navigate = useNavigate()
  const now = dayjs()
  const currentFyStart = now.month() >= 3 ? now.year() : now.year() - 1
  const [periodMode, setPeriodMode] = useState<'month' | 'fy' | 'custom'>('month')
  const [selectedFY, setSelectedFY] = useState<number>(currentFyStart)
  const [customRange, setCustomRange] = useState<[Dayjs, Dayjs]>([now.subtract(29, 'day'), now])
  const currentUser = authStore.getUser()
  const isAdmin = authStore.isAdmin()
  const [notifications, setNotifications] = useState<NotificationItem[]>(() => notificationStore.getAll())
  const [searchText, setSearchText] = useState('')
  const [filterTrigger, setFilterTrigger] = useState<string>('')
  const [filterStatus, setFilterStatus] = useState<string>('')
  const [filterChannel, setFilterChannel] = useState<string>('')

  useEffect(() => {
    // Hydrate from PostgreSQL on mount (DB is the source of truth)
    notificationStore.refresh().then(() => setNotifications(notificationStore.getAll()))

    const handleStoreUpdate = () => {
      setNotifications(notificationStore.getAll())
    }

    window.addEventListener(notificationStore.eventName, handleStoreUpdate)
    return () => window.removeEventListener(notificationStore.eventName, handleStoreUpdate)
  }, [])

  const periodStart = periodMode === 'fy'
    ? dayjs(`${selectedFY}-04-01`).startOf('day')
    : periodMode === 'custom'
    ? customRange[0].startOf('day')
    : now.startOf('month')
  const periodEnd = periodMode === 'fy'
    ? dayjs(`${selectedFY + 1}-03-31`).endOf('day')
    : periodMode === 'custom'
    ? customRange[1].endOf('day')
    : now.endOf('month')
  const periodTitle = periodMode === 'fy'
    ? `FY ${selectedFY}-${String(selectedFY + 1).slice(-2)}`
    : periodMode === 'custom'
    ? `${customRange[0].format('DD MMM')} – ${customRange[1].format("DD MMM 'YY")}`
    : now.format('MMM YYYY')

  // Scope to the signed-in user FIRST — everything below (counts, "mark all
  // read", the table) derives from `visible`, so a non-admin can never see,
  // count, or act on a notification addressed to someone else.
  const visible = notifications.filter((n) => isForUser(n, currentUser, isAdmin))

  const filtered = visible.filter((n) => {
    const createdAt = dayjs(n.createdAt)
    const matchPeriod = createdAt.isAfter(periodStart.subtract(1, 'ms')) && createdAt.isBefore(periodEnd.add(1, 'ms'))
    const matchSearch =
      n.title.toLowerCase().includes(searchText.toLowerCase()) ||
      n.recipient.toLowerCase().includes(searchText.toLowerCase()) ||
      n.message.toLowerCase().includes(searchText.toLowerCase())
    const matchTrigger = !filterTrigger || n.eventTrigger === filterTrigger
    const matchStatus = !filterStatus || n.status === filterStatus
    const matchChannel = !filterChannel || n.channels.includes(filterChannel as DeliveryChannel)
    return matchPeriod && matchSearch && matchTrigger && matchStatus && matchChannel
  })

  const totalCount = visible.length
  const queuedCount = visible.filter((n) => n.status === 'Queued').length
  const sentToday = visible.filter((n) => n.createdAt.startsWith(new Date().toISOString().slice(0, 10)) && n.status === 'Sent').length
  // Unread = anything the user hasn't read yet (Sent / Queued / Unread) — same
  // rule the bell badge uses, so the two never disagree.
  const pendingCount = visible.filter((n) => isUnread(n.status)).length

  const markAsRead = (id: string) => {
    notificationStore.save({ id, status: 'Read' })
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, status: 'Read' } : n))
    )
    message.success('Marked as read')
  }

  const markAllRead = () => {
    visible.filter((n) => isUnread(n.status)).forEach((n) => notificationStore.save({ id: n.id, status: 'Read' }))
    setNotifications((prev) => prev.map((n) => (isUnread(n.status) ? { ...n, status: 'Read' } : n)))
    message.success('All notifications marked as read')
  }

  const deleteNotification = (id: string) => {
    notificationStore.remove(id)
    setNotifications((prev) => prev.filter((n) => n.id !== id))
    message.success('Notification deleted')
  }

  const processQueue = () => {
    NotificationEngine.processQueue()
    setNotifications(notificationStore.getAll())
    message.success('Queue processed')
  }

  const columns = [
    {
      title: 'Trigger Event',
      dataIndex: 'eventTrigger',
      key: 'eventTrigger',
      width: 170,
      render: (type: string) => (
        <Tag color={TRIGGER_COLORS[type] || 'default'}>{(type || 'SYSTEM_ALERT').replace(/_/g, ' ')}</Tag>
      ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text: string, record: NotificationItem) => {
        const msgParts = (record.message || '').split('|||LEAD_ID:')
        const displayMsg = msgParts[0]
        const actionLeadId = msgParts[1]

        return (
          <div>
            <div style={{ fontWeight: isUnread(record.status) ? 700 : 500, color: '#0B1426', fontSize: 13.5 }}>
              {isUnread(record.status) && (
                <Badge dot style={{ marginRight: 6 }} />
              )}
              {text}
            </div>
            <div style={{ fontSize: 12, color: '#8A93A6', marginTop: 2 }}>{displayMsg}</div>
            {actionLeadId && (
               <div style={{ marginTop: 4 }}>
                 <a href={`/leads/${actionLeadId}`} style={{ fontSize: 12, fontWeight: 500, color: '#00A651' }}>
                    View Lead →
                 </a>
               </div>
            )}
          </div>
        )
      },
    },
    {
      title: 'Recipient',
      dataIndex: 'recipient',
      key: 'recipient',
      width: 140,
      render: (r: string) => <span style={{ fontSize: 13, color: '#4A5568' }}>{r}</span>,
    },
    {
      title: 'Channels',
      dataIndex: 'channels',
      key: 'channels',
      width: 140,
      render: (channels: DeliveryChannel[]) => (
        <Space size={4} wrap>
          {(channels || []).map(c => <Tag key={c} color={CHANNEL_COLORS[c] || 'default'} style={{ margin: 0 }}>{c}</Tag>)}
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status: string) => {
        let color = 'default'
        if (status === 'Queued') color = 'warning'
        if (status === 'Sent') color = 'success'
        if (status === 'Unread') color = 'processing'
        if (status === 'Read') color = 'default'
        
        return (
          <Tag color={color} style={{ fontWeight: 600 }}>
            {status}
          </Tag>
        )
      },
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (d: string) => <span style={{ fontSize: 12.5, color: '#8A93A6' }}>{new Date(d).toLocaleString()}</span>,
    },
    {
      title: 'Actions',
      key: 'action',
      width: 120,
      render: (_: unknown, record: NotificationItem) => (
        <Space size="small">
          {isUnread(record.status) && (
            <Button
              type="link"
              size="small"
              icon={<CheckOutlined />}
              title="Mark as Read"
              onClick={() => markAsRead(record.id)}
            />
          )}
          <Popconfirm
            title="Delete Notification"
            description="Are you sure you want to delete this notification?"
            onConfirm={() => deleteNotification(record.id)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button type="link" danger size="small" icon={<DeleteOutlined />} title="Delete" />
          </Popconfirm>
        </Space>
      ),
    },
  ]

  if (!notificationStore.loaded) return <TableSkeleton columns={7} />

  return (
    <div style={{ width: '100%' }}>
      {/* Page Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: '#001A4D', letterSpacing: '-0.02em' }}>
            Notifications Engine
          </h2>
          <p style={{ fontSize: 13, color: '#8A93A6', margin: '3px 0 0' }}>
            Manage alerts, reminders, and system notifications. Deliveries strictly respect office hours (9 AM - 6 PM).
          </p>
        </div>
        <Space>
          <Button icon={<SendOutlined />} onClick={processQueue} disabled={queuedCount === 0}>
            Process Queue ({queuedCount})
          </Button>
          <Button icon={<CheckCircleOutlined />} onClick={markAllRead} disabled={pendingCount === 0}>
            Mark All Read
          </Button>
        </Space>
      </div>

      {/* KPI Cards */}
      <KpiCardRow
        cards={[
          {
            label: 'Total Notifications',
            value: totalCount,
            icon: <BellOutlined style={{ fontSize: 24, color: '#001A4D' }} />,
            iconBg: 'rgba(0,26,77,0.08)',
            borderColor: '#001A4D',
          },
          {
            label: 'Queued (Off-Hours)',
            value: queuedCount,
            icon: <ClockCircleOutlined style={{ fontSize: 24, color: '#D97706' }} />,
            iconBg: 'rgba(217,119,6,0.08)',
            borderColor: '#D97706',
          },
          {
            label: 'Sent Today',
            value: sentToday,
            icon: <MailOutlined style={{ fontSize: 24, color: '#00A651' }} />,
            iconBg: 'rgba(0,166,81,0.08)',
            borderColor: '#00A651',
          },
          {
            label: 'Unread',
            value: pendingCount,
            icon: <BellOutlined style={{ fontSize: 24, color: '#0099CC' }} />,
            iconBg: 'rgba(0,153,204,0.08)',
            borderColor: '#0099CC',
          },
        ]}
      />

      {/* Period Filter */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#4B5563', display: 'flex', alignItems: 'center', gap: 6 }}>
          <CalendarOutlined /> Period
        </span>
        <Select
          value={periodMode === 'fy' ? `fy_${selectedFY}` : periodMode}
          onChange={(val: string) => {
            if (val === 'month' || val === 'custom') {
              setPeriodMode(val as 'month' | 'custom')
            } else {
              setPeriodMode('fy')
              setSelectedFY(parseInt(val.replace('fy_', ''), 10))
            }
          }}
          style={{ minWidth: 200 }}
        >
          <Select.Option value="month">Month ({now.format('MMM YYYY')})</Select.Option>
          <Select.OptGroup label="Financial Year">
            {[-1, 0, 1].map(offset => {
              const fy = currentFyStart + offset
              return (
                <Select.Option key={`fy_${fy}`} value={`fy_${fy}`}>
                  FY {fy}-{String(fy + 1).slice(-2)}
                </Select.Option>
              )
            })}
          </Select.OptGroup>
          <Select.OptGroup label="Custom">
            <Select.Option value="custom">Custom Date Range</Select.Option>
          </Select.OptGroup>
        </Select>
        {periodMode === 'custom' && (
          <DatePicker.RangePicker
            value={customRange}
            onChange={(v) => v && v[0] && v[1] && setCustomRange([v[0], v[1]])}
            allowClear={false}
            format="DD MMM YYYY"
            style={{ borderRadius: 9 }}
          />
        )}
        <span style={{ fontSize: 13, color: '#8A93A6' }}>Showing data for: <b style={{ color: '#001A4D' }}>{periodTitle}</b></span>
      </div>

      {/* Filters */}
      <Card
        bordered={false}
        style={{ borderRadius: 8, marginBottom: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: '1px solid #E4E8F0' }}
        bodyStyle={{ padding: 14 }}
      >
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12} md={7}>
            <Input.Search
              placeholder="Search title, recipient, message..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Select
              placeholder="Trigger Event"
              allowClear
              value={filterTrigger || undefined}
              onChange={setFilterTrigger}
              style={{ width: '100%' }}
              options={Object.keys(TRIGGER_COLORS).map((t) => ({ label: t.replace(/_/g, ' '), value: t }))}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Status"
              allowClear
              value={filterStatus || undefined}
              onChange={setFilterStatus}
              style={{ width: '100%' }}
              options={[
                { label: 'Sent', value: 'Sent' },
                { label: 'Queued', value: 'Queued' },
                { label: 'Unread', value: 'Unread' },
                { label: 'Read', value: 'Read' },
              ]}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Channel"
              allowClear
              value={filterChannel || undefined}
              onChange={setFilterChannel}
              style={{ width: '100%' }}
              options={[
                { label: 'In-App', value: 'In-App' },
                { label: 'Email', value: 'Email' },
                { label: 'WhatsApp', value: 'WhatsApp' },
              ]}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Button
              block
              onClick={() => {
                setSearchText('')
                setFilterTrigger('')
                setFilterStatus('')
                setFilterChannel('')
                setPeriodMode('month')
                setSelectedFY(currentFyStart)
              }}
            >
              Clear
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Table */}
      <Card bordered={false} style={{ borderRadius: 10, boxShadow: '0 1px 6px rgba(0,0,0,0.08)' }}>
        <div style={{ fontSize: 12, color: '#8A93A6', marginBottom: 10, textAlign: 'right' }}>
          Showing <strong>{filtered.length}</strong> of <strong>{visible.length}</strong> notifications
        </div>
        <Table
          dataSource={filtered.sort((a,b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())}
          columns={columns}
          rowKey="id"
          pagination={{ defaultPageSize: 10, pageSizeOptions: ['10', '25', '50', '100'], showSizeChanger: true, showTotal: (total, range) => `${range[0]}–${range[1]} of ${total}` }}
          rowClassName={(record) => isUnread(record.status) ? 'unread-notification-row' : ''}
        />
      </Card>
    </div>
  )
}

export default NotificationList
